import { queryOptions, useQuery } from '@tanstack/react-query';

import type { DateRange } from '@cast/shared-types';
import { OrganizationClustersReportResponse } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const ORGANIZATION_CLUSTERS_REPORT_QUERY_KEY = 'organization-clusters-report';

const getOrganizationClustersReportQueryKey = (
  from: { toISOString(): string },
  to: { toISOString(): string },
  useListingPrices: boolean
) =>
  [
    ORGANIZATION_CLUSTERS_REPORT_QUERY_KEY,
    from.toISOString(),
    to.toISOString(),
    useListingPrices,
  ] as const;

type OrganizationClustersReportQuery = {
  range: DateRange;
  useListingPrices: boolean;
  enabled?: boolean;
};

type UseOrganizationClustersReportQuery = {
  data?: OrganizationClustersReportResponse;
  isLoading: boolean;
  error?: unknown;
  refetch: () => void;
};

const getOrganizationClustersReportQueryOptions = ({
  range: [from, to],
  useListingPrices,
  enabled = true,
}: OrganizationClustersReportQuery) =>
  queryOptions({
    queryKey: getOrganizationClustersReportQueryKey(from, to, useListingPrices),
    queryFn: async () => {
      const { data } = await apiClient.costReport.getOrganizationClustersReport(
        {
          startTime: from.toISOString(),
          endTime: to.toISOString(),
          useListingPrices,
        }
      );
      return data;
    },
    enabled,
  });

export const useOrganizationClustersReportQuery = (
  props: OrganizationClustersReportQuery
): UseOrganizationClustersReportQuery => {
  const { data, isLoading, error, refetch } = useQuery(
    getOrganizationClustersReportQueryOptions(props)
  );
  return { data, isLoading, error, refetch };
};
